import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { logAction } from '../shared/lib/userLogger';
import { QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { queryClient } from '../shared/lib/queryClient';
import { AuthProvider, useAuth } from '../features/auth/model/authStore';
import { BoardProvider } from '../entities/board';
import { LeadsProvider } from '../entities/lead/model/api';
import { LoginForm } from '../features/auth';
import { KanbanPage } from '../pages/kanban';
import { TasksPage } from '../pages/tasks';
import { AdminPage } from '../pages/admin';
import { AnalyticsPage } from '../pages/analytics';
import { TagsPage } from '../pages/tags';
import { KnowledgeBasePage } from '../pages/knowledge-base';
import { IntScorozvonPage } from '../pages/int-scorozvon';
import { LeaderBoardPage } from '../pages/leader-board';
import { ProfilePage } from '../pages/profile';
import { ResetPasswordPage } from '../pages/reset-password';
import { PartnerPage } from '../pages/partner';
import { PartnerAuthProvider } from '../features/partner-auth';
import { ThemeProvider } from '../features/theme-provider';
import { ErrorFallback } from '../shared/ui/ErrorFallback';

const KanbanCardRedirect: React.FC = () => {
  const { cardId } = useParams<{ cardId: string }>();
  return <Navigate to={`/kanban?card=${cardId}`} replace />;
};

const LoginRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isReady, currentUser } = useAuth();
  if (!isReady) return null;
  if (isAuthenticated) {
    if (currentUser?.needResetPass) return <Navigate to="/reset-password" replace />;
    return <Navigate to="/kanban" replace />;
  }
  return <>{children}</>;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isReady, currentUser } = useAuth();
  if (!isReady) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (currentUser?.needResetPass) return <Navigate to="/reset-password" replace />;
  return <>{children}</>;
};

const PasswordResetRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isReady, currentUser } = useAuth();
  if (!isReady) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!currentUser?.needResetPass) return <Navigate to="/kanban" replace />;
  return <>{children}</>;
};

const ManagerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isReady, currentUser } = useAuth();
  if (!isReady) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (currentUser?.needResetPass) return <Navigate to="/reset-password" replace />;
  const role = currentUser?.role;
  if (role !== 'ADMIN' && role !== 'MANAGER') return <Navigate to="/kanban" replace />;
  return <>{children}</>;
};

const wrap = (element: React.ReactNode) => (
  <ErrorBoundary FallbackComponent={ErrorFallback}>
    {element}
  </ErrorBoundary>
);

const PAGE_NAMES: Record<string, string> = {
  '/kanban':       'Kanban',
  '/tasks':        'Tasks',
  '/analytics':    'Analytics',
  '/tags':         'Tags',
  '/knowledge-base': 'Knowledge Base',
  '/profile':      'Profile',
  '/leader-board': 'Leader Board',
  '/int-scorozvon':'Scorozvon integration',
  '/admin':        'Admin',
  '/login':        'Login',
  '/reset-password': 'Reset Password',
};

const RouteLogger: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    const base = '/' + location.pathname.split('/').filter(Boolean)[0];
    const name = PAGE_NAMES[base] ?? location.pathname;
    logAction(`Visited page: ${name}`, 'URL');
  }, [location.pathname]);
  return null;
};

const AppRoutes: React.FC = () => (
  <>
  <RouteLogger />
  <Routes>
    <Route path="/login" element={wrap(
      <LoginRoute><LoginForm /></LoginRoute>
    )} />
    <Route path="/kanban" element={wrap(
      <ProtectedRoute>
        <BoardProvider>
          <LeadsProvider>
            <KanbanPage />
          </LeadsProvider>
        </BoardProvider>
      </ProtectedRoute>
    )} />
    <Route path="/tasks" element={wrap(
      <ProtectedRoute>
        <BoardProvider>
          <LeadsProvider>
            <TasksPage />
          </LeadsProvider>
        </BoardProvider>
      </ProtectedRoute>
    )} />
    <Route path="/analytics" element={wrap(
      <ManagerRoute><AnalyticsPage /></ManagerRoute>
    )} />
    <Route path="/profile" element={wrap(
      <ProtectedRoute><ProfilePage /></ProtectedRoute>
    )} />
    <Route path="/reset-password" element={wrap(
      <PasswordResetRoute><ResetPasswordPage /></PasswordResetRoute>
    )} />
    <Route path="/tags" element={wrap(
      <ManagerRoute><TagsPage /></ManagerRoute>
    )} />
    <Route path="/int-scorozvon" element={wrap(
      <ManagerRoute><IntScorozvonPage /></ManagerRoute>
    )} />
    <Route path="/leader-board" element={wrap(
      <ProtectedRoute><LeaderBoardPage /></ProtectedRoute>
    )} />
    <Route path="/knowledge-base" element={wrap(
      <ProtectedRoute><KnowledgeBasePage /></ProtectedRoute>
    )} />
    <Route path="/admin" element={<Navigate to="/admin/stats" replace />} />
    <Route path="/admin/:section" element={wrap(
      <ManagerRoute><AdminPage /></ManagerRoute>
    )} />
    <Route path="/kanban/:cardId" element={wrap(
      <ProtectedRoute><KanbanCardRedirect /></ProtectedRoute>
    )} />
    <Route path="/partner" element={wrap(
      <PartnerAuthProvider><PartnerPage /></PartnerAuthProvider>
    )} />
    <Route path="*" element={<Navigate to="/kanban" />} />
  </Routes>
  </>
);

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export { App };
